import { useState } from 'react';
import { useExecutiveData, ExecutiveFilters } from '../../../hooks/useExecutiveData';
import { ExecutiveFiltersBar } from './components/ExecutiveFilters';
import { ExecutiveOverview } from './components/ExecutiveOverview';
import { EngagementSection } from './components/EngagementSection';
import { FinancialHealthSection, BehaviorSection } from './components/FinancialBehaviorSection';
import { DistributionSection } from './components/DistributionSection';
import { AccessSection } from './components/AccessSection';

type Tab = 'overview' | 'engagement' | 'financial' | 'behavior' | 'distribution' | 'access';

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'overview',     label: 'Visão Geral',      icon: 'ri-dashboard-3-line'      },
  { id: 'engagement',   label: 'Engajamento',       icon: 'ri-pulse-line'            },
  { id: 'financial',    label: 'Saúde Financeira',  icon: 'ri-heart-pulse-line'      },
  { id: 'behavior',     label: 'Comportamento',     icon: 'ri-pie-chart-2-line'      },
  { id: 'distribution', label: 'Distribuições',     icon: 'ri-bar-chart-grouped-line'},
  { id: 'access',       label: 'Acessos',           icon: 'ri-login-box-line'        },
];

const ExecutivePage = () => {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [filters, setFilters] = useState<ExecutiveFilters>({
    period: '30d',
    planId: null,
    userStatus: 'all',
  });

  const { data, loading } = useExecutiveData(filters);

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-[24px] font-700 text-white flex items-center gap-2.5">
              <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-violet-600/20">
                <i className="ri-line-chart-line text-violet-400 text-[18px]" />
              </div>
              Visão Executiva
            </h1>
            <p className="text-white/40 text-[14px] mt-1.5 max-w-xl">
              Indicadores agregados e anônimos da base de usuários — engajamento e saúde financeira da plataforma.
            </p>
          </div>
          <div className="flex items-center gap-2 px-3.5 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-shield-check-line text-emerald-400 text-[15px]" />
            </div>
            <div>
              <p className="text-[12px] font-600 text-emerald-400">Conformidade LGPD</p>
              <p className="text-[11px] text-emerald-400/60">Apenas dados agregados e anônimos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <ExecutiveFiltersBar filters={filters} plans={data.plans} onChange={setFilters} />

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-1">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-500 whitespace-nowrap cursor-pointer transition-all duration-150 ${
              activeTab === tab.id
                ? 'bg-violet-600/20 text-violet-300 border border-violet-500/30'
                : 'text-white/50 hover:bg-white/5 hover:text-white border border-transparent'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${tab.icon} text-[14px]`} />
            </div>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {/* AccessSection manages its own loading state — don't block it with useExecutiveData's loading */}
      {activeTab === 'access' ? (
        <AccessSection period={filters.period} />
      ) : loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <i className="ri-loader-4-line text-3xl text-violet-400 animate-spin" />
          <p className="text-white/40 text-[14px]">Calculando indicadores agregados...</p>
        </div>
      ) : (
        <>
          {activeTab === 'overview' && (
            <ExecutiveOverview kpis={data.kpis} alerts={data.alerts} />
          )}
          {activeTab === 'engagement' && (
            <EngagementSection
              engagement={data.engagement}
              totalUsers={data.kpis.totalUsers}
            />
          )}
          {activeTab === 'financial' && (
            <FinancialHealthSection health={data.financialHealth} />
          )}
          {activeTab === 'behavior' && (
            <BehaviorSection behavior={data.behavior} />
          )}
          {activeTab === 'distribution' && (
            <DistributionSection distribution={data.distribution} />
          )}
        </>
      )}
    </div>
  );
};

export default ExecutivePage;
